import { useState, useEffect } from 'preact/hooks'

import { ControllerAttributes, PricehelperResponseData } from './types.ts'
import { getBaseUrl } from '../helpers/getBaseUrl.ts'
import { generateGtmScriptNode } from './generateGtmScriptNode.ts'

declare global {
  interface Window {
    walley: any
  }
}

interface PartPaymentParameters {
  token: string
  url: string
  maxPayment: number
  minPayment: number
}

const ProductPriceBlockController = ({
  // articles,
  articleErpItemNumber,
  channelId,
  category,
  subCategory,
  weeksUntilDelivery,
  bom,
  awaitBomBeforePricehelperRequest,
  structSku,
}: ControllerAttributes) => {
  const ProductPriceBlock: HTMLElement | null = document.getElementById('productPriceBlock')
  const baseUrl = getBaseUrl(channelId)

  if (!baseUrl) throw new Error(`Unsuccessful attempt to fetch baseUrl for channel ${channelId}`)

  try {
    if (ProductPriceBlock) {
      ProductPriceBlock?.setAttribute(
        'article-numbers',
        articleErpItemNumber.split(' & ').join(', ')
      )
    } else {
      throw new Error(
        'This controller component requires the product-price-block web conponent set up with id ProductPriceBlock'
      )
    }
  } catch (error) {
    console.error(error)
  }

  const isValidJson = (jsonString: string) => {
    try {
      JSON.parse(jsonString)
      return true
    } catch (e) {
      return false
    }
  }

  const [PricehelperResponseData, setPricehelperResponseData] = useState<
    PricehelperResponseData | undefined
  >(undefined)
  const [quantityFactor, setQuantityFactor] = useState<number>(1)
  const [partPaymentParameters, setPartPaymentParameters] = useState<PartPaymentParameters | null>(
    null
  )
  const [disabledInPim, setDisabledInPim] = useState<boolean>(false)
  const [disabledInLfc, setDisabledInLfc] = useState<boolean>(false)

  const fetchPriceAndAvailability = async (payload: {
    articles: { qty: Number; sku: string | never[] }[]
  }) => {
    const pricehelperResponse = fetch(`${baseUrl}api/v1/pricehelper`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        return response.json()
      })
      .catch((error) => console.error('Fetch error:', error))

    const response = await pricehelperResponse
    
    setPricehelperResponseData(response)
  }

  useEffect(() => {
    try {
      const awaitBom =
        awaitBomBeforePricehelperRequest &&
        isValidJson(awaitBomBeforePricehelperRequest) &&
        JSON.parse(awaitBomBeforePricehelperRequest)
  
      if (awaitBom) {
        if (!bom) {
          return
        }
      }
  
      const articlesArray = articleErpItemNumber.split(' & ').map((articleId: string) => ({ articleId }))
      const articlesForPriceAndAvailabilityRequest =
        bom && isValidJson(bom) ? JSON.parse(bom) : articlesArray
      const payload = articlesForPriceAndAvailabilityRequest.map(
        (article: { articleId: string; quantity: number; pricingInfo: string }) => ({
          sku: article.articleId,
          qty: (article.quantity || 1) * quantityFactor,
          ...(bom && isValidJson(bom) && { overridePricing: article.pricingInfo }),
        })
      )
      fetchPriceAndAvailability({ articles: payload })
    } catch (error) {
      console.error('Error in ProductPriceBlockController:', error)
    }
  }, [articleErpItemNumber, quantityFactor, bom, awaitBomBeforePricehelperRequest])

  useEffect(() => {
    const handleQuantityChange = async (event: Event) => {
      if (event instanceof CustomEvent && event.detail && event.detail.qty) {
        setQuantityFactor(event.detail.qty)
      }
    }

    (document as EventTarget).addEventListener(
      'product-price-block-quantity-change',
      handleQuantityChange
    )

    return () => {
      (document as EventTarget).removeEventListener(
        'product-price-block-quantity-change',
        handleQuantityChange
      )
    }
  }, [])

  const addToCart = async () => {
    const handleCartUpdate = () => {
      const ProductPriceBlock: HTMLElement | null = document.getElementById('productPriceBlock')

      if (!ProductPriceBlock) {
        throw Error('Cannot find element with id ProductPriceBlock')
      }

      const event = new CustomEvent('product-price-block-updated-cart', {
        bubbles: true,
        cancelable: true,
      })

      ProductPriceBlock.dispatchEvent(event)
    }

      const parsedBom = bom && isValidJson(bom) ? JSON.parse(bom) : null
      const articlesArray = parsedBom?.map(
        (payload: {
          articleId: string
          configString: any
          editorial: any
          // encodedChosenValuesExtended: any - currently not supported by Pricehelper
          orderRowDescription: any
          pricingInfo: string
          quantity: number
          weightVolumeDimensions: any
        }) =>
          payload.configString
            ? {
                // chosenValues: payload.encodedChosenValuesExtended,
                configString: payload.configString,
                contextId: payload.articleId,
                Editorial: payload.editorial,
                orderRowDescription: payload.orderRowDescription,
                pricingInfo: payload.pricingInfo,
                Quantity: quantityFactor * payload.quantity,
                Sku: payload.articleId,
                Type: 'configProduct',
                WeightVolumeDimensions: payload.weightVolumeDimensions,
              }
            : {
                sku: payload.articleId,
                quantity: quantityFactor * payload.quantity,
                lfcItemNumber: articleErpItemNumber,
              }
      )

    if (PricehelperResponseData?.articles) {
      fetch(`${baseUrl}cart/addjson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(articlesArray ? articlesArray : PricehelperResponseData?.articles),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`) // TODO: [WEBBUTV-5640] Lägg till felhantering i UI
          }

          handleCartUpdate()

          return response.json()
        })
        .catch((error) => {
          console.error('Fetch error:', error)

          ProductPriceBlock?.setAttribute('failed-add-to-cart-request', JSON.stringify(error))
        })
    } else {
      console.error('Articles are missing for addtocart request.')
    }
  }

  useEffect(() => {
    ProductPriceBlock?.setAttribute(
      'articles',
      JSON.stringify(PricehelperResponseData?.articles ?? [])
    )

    const updatedProductPriceBlock = document.getElementById('productPriceBlock')
    updatedProductPriceBlock?.addEventListener(
      'product-price-block-call-to-action-button-click',
      addToCart
    )

    return () => {
      updatedProductPriceBlock?.removeEventListener(
        'product-price-block-call-to-action-button-click',
        addToCart
      )
    }
  }, [PricehelperResponseData?.articles])

  useEffect(() => {
    ProductPriceBlock?.setAttribute(
      'formated-price',
      PricehelperResponseData?.summary?.FormatedPrice || ''
    )
    ProductPriceBlock?.setAttribute(
      'formated-product-discount',
      PricehelperResponseData?.summary?.FormatedProductDiscount || ''
    )
    ProductPriceBlock?.setAttribute(
      'ordinary-price',
      PricehelperResponseData?.summary?.FormatedOrdinaryPrice?.toString() || ''
    )
    ProductPriceBlock?.setAttribute(
      'price',
      PricehelperResponseData?.summary?.Price?.toString() || ''
    )
    ProductPriceBlock?.setAttribute(
      'product-discount',
      PricehelperResponseData?.summary?.ProductDiscount?.toString() || ''
    )
    ProductPriceBlock?.setAttribute(
      'discounts',
      JSON.stringify(PricehelperResponseData?.summary?.Discounts) || ''
    )
    ProductPriceBlock?.setAttribute(
      'delivery-information',
      PricehelperResponseData?.summary?.DeliveryInformation || ''
    )
    ProductPriceBlock?.setAttribute(
      'delivery-status',
      PricehelperResponseData?.summary?.DeliveryStatus || ''
    )

    if (PricehelperResponseData?.summary?.IsAvailableForPurchase !== undefined) {
      setDisabledInPim(!PricehelperResponseData?.summary?.IsAvailableForPurchase)
    }
  }, [PricehelperResponseData?.summary])
  
  // publishLevel is a way to set an article as not buyable in lfc backend
  const publishLevel = bom && JSON.parse(bom)[0].publishLevel
  if (publishLevel) {
    setDisabledInLfc(publishLevel === '80')
  }

  useEffect(() => {
    if (disabledInPim) console.log('Article is set as not buyable in pim backend')
    if (disabledInLfc) console.log('Article is set as not buyable in lfc backend')

    ProductPriceBlock?.setAttribute(
      'available-for-purchase',
      disabledInPim || disabledInLfc ? 'false' : 'true'
    )
  }, [disabledInPim, disabledInLfc])

  useEffect(() => {
    const getPartPaymentParameters = async () => {
      try {
        const partPaymentProviderApiUrl = `${
          import.meta.env.VITE_API_URL
        }Payment/PartPaymentProviderWidget`

        const response = await fetch(`${partPaymentProviderApiUrl}?channelId=${channelId}`, {
          method: 'GET',
        })
        if (!response.ok) {
          throw new Error(`Response status: ${response.status}`)
        }
        return await response.json()
      } catch (error: any) {
        console.error(error.message)
        return null
      }
    }

    const fetchPartPaymentParameters = async () => {
      const parameters = await getPartPaymentParameters()
      setPartPaymentParameters(parameters)
    }

    fetchPartPaymentParameters()
  }, [channelId])

  useEffect(() => {
    if (PricehelperResponseData?.summary && ProductPriceBlock) {
      const event = new CustomEvent('product-price-block-received-pricehelper-summary', {
        bubbles: true,
        cancelable: true,
        detail: PricehelperResponseData?.summary,
      })

      ProductPriceBlock.dispatchEvent(event)
    }
  }, [PricehelperResponseData?.summary, ProductPriceBlock])

  useEffect(() => {
    if (!ProductPriceBlock || !partPaymentParameters) return

    const widgetContainer = document.createElement('div')
    widgetContainer.id = 'collector-widget-container'
    widgetContainer.setAttribute('style', 'width: 92vw; max-width: 400px')
    ProductPriceBlock.appendChild(widgetContainer)
    widgetContainer.style.display = 'none'

    const { url, token }: PartPaymentParameters = partPaymentParameters

    if (!url || !token) return

    const script = document.createElement('script') as HTMLScriptElement
    script.id = 'collector-widget-script'
    script.src = `${url}/collector-checkout-loader.js`
    script.dataset.token = token
    script.dataset.widget = 'part-payment'
    script.dataset.containerId = 'collector-widget-container'

    const getLanguage = (channelId: string) => {
      switch (channelId) {
        case '1':
        case '2':
          return 'sv-SE'
        case '4':
        case '9':
          return 'nb-NO'
        case '7':
          return 'sv-FI'
        case '8':
          return 'fi-FI'
        default:
          return ''
      }
    }
    const language = getLanguage(channelId)
    if (language.length) {
      script.dataset.lang = language
    }

    document.body.appendChild(script)
  }, [ProductPriceBlock, partPaymentParameters])

  useEffect(() => {
    const price = PricehelperResponseData?.summary?.Price
    const widgetContainer = document.getElementById('collector-widget-container')
    if (!price || !partPaymentParameters || !widgetContainer) return

    const { minPayment, maxPayment }: PartPaymentParameters = partPaymentParameters
    if (price * 100 >= minPayment && price * 100 <= maxPayment) {
      widgetContainer.style.display = 'block'
      const script = document.getElementById('collector-widget-script')
      if (!script) return

      script.dataset.amount = (price * 100).toString()

      window?.walley?.checkout?.api?.update()
    } else {
      widgetContainer.style.display = 'none'
    }
  }, [PricehelperResponseData?.summary.Price, partPaymentParameters])

  useEffect(() => {
    if (PricehelperResponseData) {
      const script = generateGtmScriptNode(
        PricehelperResponseData,
        articleErpItemNumber,
        category,
        subCategory,
        weeksUntilDelivery,
        structSku
      )
      if (script) {
        document.body.appendChild(script)
      } else {
        throw new Error('Failed to generate GTM script for productView')
      }
    }
  }, [PricehelperResponseData])

  const loaderElement = document.getElementById('productPriceBlockSkeletonLoader')
  if (PricehelperResponseData) {
    loaderElement?.setAttribute('style', 'display: none')
  } else {
    if (loaderElement?.hasAttribute('style')) {
      loaderElement.removeAttribute('style')
    }
  }

  return null
}

export default ProductPriceBlockController
